import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { StatCard, PageHeader, Badge } from "@/components/Kit";
import { Building2, Users, IndianRupee, Send, Smartphone, TrendingUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell } from "recharts";

const inr = (n) => "₹" + (n || 0).toLocaleString("en-IN");

export default function SuperDashboard() {
  const { data, isLoading } = useQuery({
    queryKey: ["sa-dashboard"],
    queryFn: async () => (await api.get("/superadmin/dashboard")).data,
  });

  if (isLoading || !data) return <div className="text-slate-400" data-testid="sa-loading">Loading…</div>;

  const planData = ["basic", "standard", "premium"].map((p) => ({
    name: p,
    value: data.schools.filter((s) => s.plan === p).length,
  }));
  const colors = ["#06B6D4", "#4F46E5", "#F59E0B"];
  const barData = [...data.schools]
    .sort((a, b) => (b.students || 0) - (a.students || 0))
    .slice(0, 6)
    .map((s) => ({ name: s.code, students: s.students || 0 }));

  return (
    <div data-testid="super-dashboard">
      <PageHeader title="Super Admin Console" subtitle="Platform-wide performance across all schools" />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard testid="stat-schools" icon={Building2} label="Total Schools" value={data.total_schools} sub={`${data.active_schools} active · ${data.pending_schools} pending`} accent="#4F46E5" delay={0} />
        <StatCard testid="stat-students" icon={Users} label="Total Students" value={data.total_students} accent="#06B6D4" delay={0.05} />
        <StatCard testid="stat-mrr" icon={TrendingUp} label="Monthly Recurring" value={inr(data.mrr)} accent="#10B981" delay={0.1} />
        <StatCard testid="stat-revenue" icon={IndianRupee} label="Fee Collection" value={inr(data.total_revenue)} accent="#F59E0B" delay={0.15} />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="ep-card p-5 lg:col-span-2">
          <p className="mb-4 text-sm font-semibold text-white">Student Load by School</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={barData}>
              <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} />
              <Tooltip contentStyle={{ background: "#1E293B", border: "1px solid #334155", borderRadius: 12, color: "#fff" }} />
              <Bar dataKey="students" fill="#4F46E5" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="ep-card p-5">
          <p className="mb-4 text-sm font-semibold text-white">Plan Distribution</p>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={planData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={4}>
                {planData.map((_, i) => <Cell key={i} fill={colors[i]} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#1E293B", border: "1px solid #334155", borderRadius: 12, color: "#fff" }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-2 flex justify-center gap-4 text-xs capitalize text-slate-400">
            {planData.map((p, i) => (
              <span key={p.name} className="flex items-center gap-1">
                <span className="h-2 w-2 rounded-full" style={{ background: colors[i] }} /> {p.name}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <StatCard testid="stat-whatsapp" icon={Send} label="WhatsApp Messages Sent" value={data.whatsapp_usage} sub="OTP + fee + admission alerts (live)" accent="#10B981" />
        <StatCard testid="stat-apk" icon={Smartphone} label="APK Downloads" value={data.apk_downloads} sub="From the public Get-the-App page" accent="#06B6D4" />
      </div>
    </div>
  );
}
